<template>
  <div class="checkout-promo-code">
    <template v-if="appliedPromoCode">
      <div class="checkout-promo-code__applied">
        <div class="checkout-promo-code__applied-info">
          <FontAwesomeIcon :icon="['far', 'tag']" class="text-sm" />
          <span class="checkout-promo-code__applied-code">{{
            appliedPromoCode.code
          }}</span>
          <span class="checkout-promo-code__applied-discount">
            ({{
              appliedPromoCode.discount_type === 'percentage'
                ? `${appliedPromoCode.discount_value}%`
                : formatCurrencyLeft(
                    parseFloat(appliedPromoCode.discount_value),
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                    currency,
                  )
            }})
          </span>
        </div>
        <button
          type="button"
          class="checkout-promo-code__remove-btn"
          :disabled="isRemoving"
          @click="removePromoCode"
        >
          <FontAwesomeIcon :icon="['far', 'xmark']" />
        </button>
      </div>
    </template>

    <template v-else>
      <button
        v-if="!isExpanded"
        type="button"
        class="checkout-promo-code__toggle"
        @click="isExpanded = true"
      >
        <FontAwesomeIcon :icon="['far', 'tag']" class="text-sm" />
        {{ $t('landingpage.checkout-page.promo-code.toggle') }}
      </button>

      <div v-else class="checkout-promo-code__form">
        <input
          ref="inputRef"
          v-model="code"
          type="text"
          class="checkout-promo-code__input"
          :placeholder="
            $t('landingpage.checkout-page.promo-code.input-placeholder')
          "
          :disabled="isApplying"
          @keyup.enter="applyPromoCode"
        />
        <button
          type="button"
          class="checkout-promo-code__apply-btn btn"
          :disabled="!code.trim() || isApplying"
          @click="applyPromoCode"
        >
          <template v-if="isApplying">
            <FontAwesomeIcon :icon="['far', 'spinner-third']" spin />
          </template>
          <template v-else>
            {{ $t('landingpage.checkout-page.promo-code.apply') }}
          </template>
        </button>
      </div>

      <p v-if="errorMessage" class="checkout-promo-code__error">
        {{ errorMessage }}
      </p>
    </template>
  </div>
</template>

<script setup>
import { ref, inject, computed, nextTick, watch } from 'vue'
import { postJson, useFetch } from '@/LandingPage/Checkout/useCheckoutFetch'
import { formatCurrencyLeft } from '@/composables/currency'

const eventId = inject('eventId')
const orderId = inject('orderId')
const orderDetails = inject('orderDetails')

const route = window.route

const emit = defineEmits(['applied', 'removed'])

const code = ref('')
const isExpanded = ref(false)
const isApplying = ref(false)
const isRemoving = ref(false)
const errorMessage = ref('')
const inputRef = ref(null)

const appliedPromoCode = computed(() => {
  return orderDetails.value?.promo_code ?? null
})

const currency = computed(() => {
  return orderDetails.value?.currency
})

watch(isExpanded, async (expanded) => {
  if (expanded) {
    await nextTick()
    inputRef.value?.focus()
  }
})

const applyPromoCode = async () => {
  if (!code.value.trim() || isApplying.value) return

  isApplying.value = true
  errorMessage.value = ''

  const { data, error, statusCode } = await postJson(
    route('api.checkout.promo-code.apply', {
      event: eventId,
      order: orderId.value,
    }),
    { code: code.value.trim() },
  )

  isApplying.value = false

  if (error.value) {
    if (statusCode.value === 422 && data.value?.errors?.code) {
      errorMessage.value = data.value.errors.code[0]
    } else if (statusCode.value === 429) {
      errorMessage.value = data.value?.message ?? 'Too many attempts.'
    } else {
      errorMessage.value = data.value?.message ?? 'Something went wrong.'
    }
    return
  }

  code.value = ''
  isExpanded.value = false
  emit('applied')
}

const removePromoCode = async () => {
  if (isRemoving.value) return

  isRemoving.value = true

  const { statusCode } = await useFetch(
    route('api.checkout.promo-code.remove', {
      event: eventId,
      order: orderId.value,
    }),
  ).delete()

  isRemoving.value = false

  if (statusCode.value === 204 || statusCode.value === 200) {
    emit('removed')
  }
}
</script>
